import { X } from "lucide-react";
import type { QuerygenAssistantMetadata } from "../types";

type QuerygenDebugDialogProps = {
  open: boolean;
  onClose: () => void;
  metadata: QuerygenAssistantMetadata | null;
};

const prettyJson = (value: string | undefined) => {
  if (!value) {
    return "";
  }

  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

export function QuerygenDebugDialog({
  open,
  onClose,
  metadata,
}: QuerygenDebugDialogProps) {
  if (!open || !metadata) {
    return null;
  }

  const toolTrace = metadata.debug?.toolTrace ?? [];
  const reasoning = metadata.debug?.reasoning ?? [];
  const dataArtifacts = Array.isArray(metadata.dataArtifacts)
    ? metadata.dataArtifacts
    : metadata.dataArtifact
      ? [metadata.dataArtifact]
      : [];
  const queries = dataArtifacts
    .map((artifact) => artifact?.query)
    .filter((query): query is string => Boolean(query));
  const query = metadata.debug_information?.query ?? queries[queries.length - 1] ?? "";
  const analysis = metadata.debug_information?.analysis ?? "";
  const llmOutput = metadata.debug?.llmOutput ?? "";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(17,17,17,0.38)] px-4 py-8">
      <div className="flex max-h-[88vh] w-full max-w-[980px] flex-col overflow-hidden rounded-md bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-[rgba(17,17,17,0.08)] px-6 py-5">
          <div>
            <div className="text-[18px] font-semibold tracking-[-0.03em] text-[#111111]">
              Debug Information
            </div>
            <div className="mt-1 text-[13px] text-[#6b7280]">
              SQL, tool trace, and model reasoning for this assistant response.
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full text-[#6b7280] transition hover:bg-[rgba(17,17,17,0.05)] hover:text-[#111111]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="thin-scrollbar flex-1 overflow-y-auto px-6 py-5">
          <div className="space-y-6">
            <section>
              <div className="mb-2 text-[12px] font-medium uppercase tracking-[0.16em] text-[#9ca3af]">
                {queries.length > 1 ? "SQL Queries" : "SQL Query"}
              </div>
              {queries.length > 1 ? (
                <div className="space-y-3">
                  {queries.map((item, index) => (
                    <pre
                      key={`${index}-${item.slice(0, 24)}`}
                      className="overflow-x-auto rounded-md bg-[#0f172a] p-4 text-[12px] leading-6 text-[#e2e8f0]"
                    >
                      <code>{item}</code>
                    </pre>
                  ))}
                </div>
              ) : (
                <pre className="overflow-x-auto rounded-md bg-[#0f172a] p-4 text-[12px] leading-6 text-[#e2e8f0]">
                  <code>{query || "No final SQL query recorded."}</code>
                </pre>
              )}
            </section>

            {analysis ? (
              <section>
                <div className="mb-2 text-[12px] font-medium uppercase tracking-[0.16em] text-[#9ca3af]">
                  Analysis
                </div>
                <div className="rounded-md border border-[rgba(17,17,17,0.08)] bg-[#fafafa] px-4 py-3 text-[13px] leading-6 text-[#27272a]">
                  {analysis}
                </div>
              </section>
            ) : null}

            {reasoning.length ? (
              <section>
                <div className="mb-2 text-[12px] font-medium uppercase tracking-[0.16em] text-[#9ca3af]">
                  Reasoning
                </div>
                <div className="space-y-3">
                  {reasoning.map((item, index) => (
                    <div
                      key={`${index}-${item.slice(0, 24)}`}
                      className="rounded-md border border-[rgba(17,17,17,0.08)] bg-[#fafafa] px-4 py-3 text-[13px] leading-6 text-[#27272a]"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            <section>
              <div className="mb-2 text-[12px] font-medium uppercase tracking-[0.16em] text-[#9ca3af]">
                Tool Trace
              </div>
              <div className="space-y-3">
                {toolTrace.length ? (
                  toolTrace.map((entry, index) => (
                    <div
                      key={`${entry.timestamp}-${index}`}
                      className="rounded-md border border-[rgba(17,17,17,0.08)] bg-white px-4 py-4"
                    >
                      <div className="flex items-center gap-2 text-[13px]">
                        <span className="rounded-full bg-[rgba(17,17,17,0.06)] px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.12em] text-[#52525b]">
                          {entry.event.replace("_", " ")}
                        </span>
                        <span className="font-medium text-[#18181b]">
                          {entry.toolName}
                        </span>
                      </div>

                      {entry.arguments ? (
                        <div className="mt-3">
                          <div className="mb-1 text-[11px] uppercase tracking-[0.12em] text-[#9ca3af]">
                            Arguments
                          </div>
                          <pre className="overflow-x-auto rounded-md bg-[#f8fafc] p-3 text-[12px] leading-6 text-[#334155]">
                            <code>{prettyJson(entry.arguments)}</code>
                          </pre>
                        </div>
                      ) : null}

                      {entry.output ? (
                        <div className="mt-3">
                          <div className="mb-1 text-[11px] uppercase tracking-[0.12em] text-[#9ca3af]">
                            Output
                          </div>
                          <pre className="overflow-x-auto rounded-md bg-[#f8fafc] p-3 text-[12px] leading-6 text-[#334155]">
                            <code>{prettyJson(entry.output)}</code>
                          </pre>
                        </div>
                      ) : null}

                      {entry.error ? (
                        <div className="mt-3">
                          <div className="mb-1 text-[11px] uppercase tracking-[0.12em] text-[#ef4444]">
                            Error
                          </div>
                          <pre className="overflow-x-auto rounded-md bg-[#fff1f2] p-3 text-[12px] leading-6 text-[#9f1239]">
                            <code>{entry.error}</code>
                          </pre>
                        </div>
                      ) : null}
                    </div>
                  ))
                ) : (
                  <div className="rounded-md border border-[rgba(17,17,17,0.08)] bg-[#fafafa] px-4 py-3 text-[13px] text-[#6b7280]">
                    No tool trace was captured for this response.
                  </div>
                )}
              </div>
            </section>

            {llmOutput ? (
              <section>
                <div className="mb-2 text-[12px] font-medium uppercase tracking-[0.16em] text-[#9ca3af]">
                  Raw Model Output
                </div>
                <pre className="overflow-x-auto rounded-md bg-[#f8fafc] p-4 text-[12px] leading-6 text-[#334155]">
                  <code>{llmOutput}</code>
                </pre>
              </section>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
